import {
    CommonModule
} from "@angular/common";

import {
    Component,
    Injectable,
    OnInit,
    inject,
    signal
} from "@angular/core";

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef
} from "@angular/material/dialog";

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogProgressBarComponent,
    TilbyDialogToolbarComponent
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";

import {
    ConfigurationManagerService,
    ConfigurationPreferences,
    EntityManagerService
} from "src/app/core";

import { lastValueFrom } from "rxjs";
import { MatIconModule } from "@angular/material/icon";
import { TilbyDatePipe } from "@tilby/tilby-ui-lib/pipes/tilby-date";
import { MatFormFieldModule } from "@angular/material/form-field";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";

import {
    MatListModule,
    MatSelectionListChange
} from "@angular/material/list";

import { MatRadioModule } from "@angular/material/radio";
import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";
import { MatDividerModule } from "@angular/material/divider";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { restManager } from "app/ajs-upgraded-providers";
import { MatTableModule } from "@angular/material/table";

import {
    Customers,
    Sales,
    SalesCustomer,
    SalesItems,
    SalesItemsPriceChanges
} from "tilby-models";

import { map, startWith } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { SaleUtilsService } from "src/app/features";
import { keyBy, MathUtils } from "src/app/shared/utils";
import { Mago } from "src/app/shared/model/mago.model";

const loginFieldsMap = {
    'type': 'appId',
    'account_name': 'accountName',
    'company': 'company',
    'password': 'password',
    'subscription_key': 'subscriptionKey',
    'esp_backend': 'espBackend'
}

type MagoOrdersInput = 
    { mode: 'orders' } |
    { mode: 'stock', sku?: string }

type MagoItemStock = Mago.ItemStock & {
    available?: number
    name?: string
};

const magoOrdersConfigNamespace = 'integrations.mago.orders.';

@Component({
    selector: 'app-mago-orders-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatDialogModule,
        MatDividerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatListModule,
        MatRadioModule,
        MatOptionModule,
        MatSelectModule,
        MatTableModule,
        ReactiveFormsModule,
        TilbyCurrencyPipe,
        TilbyDatePipe,
        TilbyDialogContentComponent,
        TilbyDialogProgressBarComponent,
        TilbyDialogToolbarComponent,
        TranslateModule,
    ],
    templateUrl: './mago-orders-dialog.component.html',
    styleUrls: ['./mago-orders-dialog.component.scss']
})
class MagoOrdersDialogComponent implements OnInit {
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly matDialogRef = inject(MatDialogRef);
    private readonly restManagerService = inject(restManager);
    private readonly translateService = inject(TranslateService);
    private readonly tilbyDatePipe = inject(TilbyDatePipe);
    private readonly saleUtilsService = inject(SaleUtilsService);
    private readonly data: MagoOrdersInput = inject(MAT_DIALOG_DATA);
    protected readonly title = this.data.mode === 'stock' ? 'CASHREGISTER.MAGO_ORDERS.TITLE_STOCK' : 'CASHREGISTER.MAGO_ORDERS.TITLE';

    protected loginForm?: FormGroup;
    protected operationInProgress = signal(false);
    protected message?: string;

    protected ordersForm?: FormGroup;
    protected ordersList?: Mago.SaleOrder[];
    protected selectedOrder?: Mago.SaleOrder;

    protected stockForm?: FormGroup;
    protected stockList?: MagoItemStock[];
    protected displayedStockColumns: string[] = ['fiscalYear', 'name', 'shopDescription', 'storageDescription', 'variantCode', 'finalOnHand', 'reservedSaleOrd', 'available'];

    protected customers: Customers[] = [];
    protected selectedCustomer?: Customers;
    protected filteredCustomers?: Observable<Customers[]>;

    private async createOrdersForm() {
        const ordersForm = this.formBuilder.group({
            customer: [],
        });

        this.customers = await this.entityManagerService.customers.fetchCollectionOffline().then((customers) => customers.filter(customer => customer.fidelity || customer.external_id));

        this.filteredCustomers = ordersForm.get('customer')!.valueChanges.pipe(
            startWith(''),
            map(value => this._filterCustomers(value || ''))
        );

        this.ordersForm = ordersForm;
    }

    private createStockForm() {
        const stockForm = this.formBuilder.group({
            itemCode: ['', Validators.required],
            shopType: ['OnlyOneShop', Validators.required],
        });

        this.stockForm = stockForm;

        if(this.data.mode === 'stock' && this.data.sku) {
            stockForm.patchValue({ itemCode: this.data.sku });
            this.searchStock();
        }
    }

    private _filterCustomers(value: string): Customers[] {
        if(typeof value !== 'string') {
            return this.customers;
        }

        const filterValue = value.toLowerCase().split(' ');

        let filteredCustomers = this.customers;

        for(const word of filterValue) {
            filteredCustomers = filteredCustomers.filter(customer =>
                customer.first_name?.toLowerCase().includes(word) ||
                customer.last_name?.toLowerCase().includes(word) ||
                customer.company_name?.toLowerCase().includes(word) ||
                customer.fidelity?.toLowerCase().includes(word) ||
                customer.external_id?.toLowerCase().includes(word) ||
                customer.tax_code?.toLowerCase().includes(word) ||
                customer.vat_code?.toLowerCase().includes(word) ||
                customer.phone?.toLowerCase().includes(word) ||
                customer.mobile?.toLowerCase().includes(word)
            );
        }

        return filteredCustomers;
    }

    displayCustomerName(customer?: Customers): string {
        if(!customer) {
            return '';
        }

        return customer.company_name
            ? `${customer.company_name}${customer?.fidelity ? ` (${customer.fidelity})` : ''}`
            : `${customer.first_name} ${customer.last_name}${customer?.fidelity ? ` (${customer.fidelity})` : ''}`;
    }

    customerSelected(event: any) {
        this.ordersList = undefined;
        this.selectedOrder = undefined;

        this.selectedCustomer = event.option.value;
    }

    private onAppIdChange(loginForm: FormGroup, value: string) {
        const requiredFields = {
            espBackend: ['magoWeb', 'mago4'],
            subscriptionKey: ['magoCloud', 'magoWeb'],
            company: ['mago4']
        };

        for(const [key, values] of Object.entries(requiredFields)) {
            const formControl = loginForm.get(key);
            formControl?.setValidators(values.includes(value) ? [Validators.required] : []);
            formControl?.updateValueAndValidity();
        }
    }

    private async createLoginForm() {
        this.ordersList = undefined;
        this.ordersForm = undefined;
        this.stockForm = undefined;
        this.stockList = undefined;
        this.selectedCustomer = undefined;

        const formFields: Record<string, any[]> = {};

        for (const [key, value] of Object.entries(loginFieldsMap)) {
            formFields[value] = [this.configurationManagerService.getPreference(`${magoOrdersConfigNamespace}${key}` as keyof ConfigurationPreferences), [Validators.required]];
        }

        const loginForm = this.formBuilder.group(formFields);

        //Initialize required flag for espBackend
        this.onAppIdChange(loginForm, loginForm.get('appId')?.value);

        loginForm.get('appId')?.valueChanges.subscribe((value) => {
            this.onAppIdChange(loginForm, value);
        });

        this.loginForm = loginForm;
    }

    protected createViewForm() {
        this.loginForm = undefined;

        switch(this.data.mode) {
            case 'stock':
                this.createStockForm();
                break;
            case 'orders':
            default:
                this.createOrdersForm();
                break;
        }
    }

    ngOnInit(): void {
        if (this.configurationManagerService.getPreference(`${magoOrdersConfigNamespace}account_name`)) {
            this.createViewForm();
        } else {
            this.createLoginForm();
        }
    }

    clearMessage() {
        this.message = undefined;
    }

    onOrderSelect(event: MatSelectionListChange) {
        this.selectedOrder = event.options[0]?.value;
    }

    async confirm() {
        // Check if there is a selected order or if an operation is in progress.
        if (this.operationInProgress() || !this.selectedOrder) {
            return;
        }

        try {
            // Indicate that an operation is in progress.
            this.operationInProgress.set(true);

            // Get the selected customer.
            let saleCustomer: SalesCustomer | undefined = undefined;

            if(this.selectedCustomer) {
                const { created_at, updated_at, deleted_at, id, ...customerData } = this.selectedCustomer;

                saleCustomer = {
                    ...customerData,
                    customer_id: id,
                }
            }

            // Fetch order details based on the selected order.
            const orderDetails: Mago.SaleOrderDetail[] = await this.restManagerService.post('mago/api/retailcorepos/OpenSalesOrdersDetails', {
                InternalOrdNo: this.selectedOrder.InternalOrdNo
            }).then((response: Mago.SaleOrderDetailsResponse) => response.saleOrdersDetails);

            // Get the sale template for the current sale.
            const saleTemplate = await this.saleUtilsService.getSaleTemplate({ skipFiscalProvider: true });

            // Prepare the update data for the sale template.
            const updateData: Partial<Sales> = {
                bill_lock: true,
                name: `Mago ${this.selectedOrder.Customer}: ${this.selectedOrder.InternalOrdNo}`.slice(0, 30),
                channel: 'mago',
                external_id: this.selectedOrder.SaleOrderId ? `${this.selectedOrder.InternalOrdNo}_${this.selectedOrder.SaleOrderId}` : this.selectedOrder.InternalOrdNo,
                notes: `OrderDate: ${this.tilbyDatePipe.transform(this.selectedOrder.OrderDate, 'dd/MM/yyyy')}\n`,
                sale_customer: saleCustomer
            }

            // Merge the update data into the sale template.
            Object.assign(saleTemplate, updateData);

            // Fetch departments offline and map them by VAT.
            const departmentsByCode = await this.entityManagerService.departments.fetchCollectionOffline().then((deps) => keyBy(deps, (dep) => dep.code));
            const itemsBySku = await this.entityManagerService.items.fetchCollectionOffline().then((items) => keyBy(items, (item) => item.sku));

            const saleItems: SalesItems[] = [];

            // Create sale items from the order details.
            for (const magoItem of orderDetails) {
                const department = departmentsByCode[magoItem.TaxCode];
                const item = itemsBySku[magoItem.Item];

                // Skip if the department is not found.
                if (!department) {
                    throw { error: 'MISSING_DEPARTMENT', tax_code: magoItem.TaxCode };
                }

                const vat = department.vat?.value || 0;

                // Calculate the item price including tax.
                const itemPrice = magoItem.NetOfTax ? MathUtils.round(magoItem.UnitValue * (1 + (vat / 100))) : magoItem.UnitValue;
                const saleItem = this.saleUtilsService.getDynamicSaleItemTemplate(department, itemPrice);

                const updateData: Partial<SalesItems> = {
                    name: magoItem.Description,
                    barcode: magoItem.BarcodeVariant || undefined,
                    category_id: item?.category_id,
                    category_name: item?.category?.name,
                    combination_sku: magoItem.VariantCode || undefined,
                    item_id: item?.id,
                    quantity: MathUtils.round(magoItem.Qty - (magoItem.DeliveredQty || 0), 3),
                    sku: magoItem.Item
                };

                Object.assign(saleItem, updateData);

                const priceChanges: SalesItemsPriceChanges[] = [];

                // If there is a discount, add it to the sale item.
                if(magoItem.DiscountAmount) {
                    const priceChange = this.saleUtilsService.getGenericPriceChange(
                        saleItem,
                        'discount_fix',
                        magoItem.NetOfTax ? MathUtils.round(magoItem.DiscountAmount * (1 + (vat / 100))) : magoItem.DiscountAmount,
                        this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.DISCOUNT')
                    );

                    priceChanges.push(priceChange);
                }

                if(magoItem.DistributedDiscount) {
                    const priceChange = this.saleUtilsService.getGenericPriceChange(
                        saleItem,
                        'discount_fix',
                        magoItem.NetOfTax ? MathUtils.round(magoItem.DistributedDiscount * (1 + (vat / 100))) : magoItem.DistributedDiscount,
                        this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.DISCOUNT')
                    );

                    priceChanges.push(priceChange);
                }

                saleItem.price_changes = priceChanges;

                // Add the created sale item to the list.
                saleItems.push(saleItem);
            }

            // Assign the created sale items to the sale template.
            saleTemplate.sale_items = saleItems;

            // Close the dialog and return the sale template.
            this.matDialogRef.close(saleTemplate);
        } catch(err: any) {
            if('tax_code' in err) {
                this.message = this.translateService.instant('CASHREGISTER.MAGO_ORDERS.MISSING_DEPARTMENT', { taxCode: err.tax_code });
            }
        } finally {
            // Reset the operation status.
            this.operationInProgress.set(false);
        }
    }

    async onLogin() {
        if (this.operationInProgress() || !this.loginForm?.valid) {
            return;
        }

        this.operationInProgress.set(true);

        try {
            const appId = this.loginForm.get('appId')?.value;

            if (appId === 'magoCloud') {
                const espBackend = this.loginForm.get('espBackend');
                espBackend?.setValue(undefined);
                espBackend?.updateValueAndValidity();
            }

            if (appId === 'mago4') {
                const subscriptionKey = this.loginForm.get('subscriptionKey');
                subscriptionKey?.setValue(undefined);
                subscriptionKey?.updateValueAndValidity();
            } else {
                const company = this.loginForm.get('company');
                company?.setValue(undefined);
                company?.updateValueAndValidity();

                // get espBackend input's value
                const espBackend = this.loginForm.get('espBackend');
                const value = espBackend?.value?.trim();

                if (value) {
                    // 1. Remove all trailing slashes
                    // 2. Remove '/mw-console' if present at the end (to avoid duplicates)
                    // 3. Add the correct and clean version

                    let cleanValue = value.replace(/\/+$/, ''); // removes trailing slashes

                    if (!cleanValue.endsWith('/mw-console')) {
                        cleanValue = `${cleanValue}/mw-console`;
                    }

                    espBackend?.setValue(`${cleanValue}/`); // Adds guaranteed trailing slash
                }
            }

            const payload: Mago.LoginRequest = {
                ...this.loginForm?.value,
                type: appId,
                appId: 'MagoAPI',
                overwrite: true
            }

            const response = await this.restManagerService.post('mago/login', payload) as Mago.LoginResponse;

            if (!response.Result) {
                throw response.Message;
            }

            for (const [key, value] of Object.entries(loginFieldsMap)) {
                const formValue = this.loginForm.get(value);

                if (formValue?.touched) {
                    if (formValue.value) {
                        await this.configurationManagerService.setShopPreferenceSync(`${magoOrdersConfigNamespace}${key}` as keyof ConfigurationPreferences, formValue.value);
                    } else {
                        await this.configurationManagerService.deleteShopPreferenceSync(`${magoOrdersConfigNamespace}${key}` as keyof ConfigurationPreferences);
                    }
                }
            }

            this.message = 'CASHREGISTER.MAGO_ORDERS.ENABLE_SUCCESSFUL';

            setTimeout(() => this.clearMessage(), 2000)

            this.createViewForm();
        } catch (error: any) {
            this.message = error?.data?.errorMessage;
        } finally {
            this.operationInProgress.set(false);
        }
    }

    async searchOrders() {
        if (this.operationInProgress() || !this.selectedCustomer) {
            return;
        }

        this.ordersList = undefined;
        this.selectedOrder = undefined;

        const searchPayload: Record<string, string | undefined> = {};

        try {
            this.operationInProgress.set(true);

            searchPayload['Customer'] = this.selectedCustomer.external_id || '';
            searchPayload['CardNumber'] = this.selectedCustomer.fidelity || '';

            const response = await this.restManagerService.post('mago/api/retailcorepos/OpenSalesOrdersList', searchPayload) as Mago.SaleOrdersResponse;

            this.ordersList = response.saleOrders;
        } catch (error: any) {
            this.message = error?.data?.errorMessage;

            if([401].includes(error.status)) {
                this.createLoginForm();
            }
        } finally {
            this.operationInProgress.set(false);
        }
    }

    async searchStock() {
        if (this.operationInProgress() || !this.stockForm?.valid) {
            return;
        }

        this.stockList = undefined;

        try {
            this.operationInProgress.set(true);

            const itemCode = this.stockForm.get('itemCode')?.value as string;
            const shopType = this.stockForm.get('shopType')?.value as Mago.ShopType;

            const response = await this.restManagerService.post('mago/api/erpinventory/getitemstock', {
                ItemCode: itemCode,
                ShopType: shopType
            }) as Mago.GetItemStockResponse;

            // Create map of item sku -> name
            const items = (await this.entityManagerService.items.fetchCollectionOffline()).filter(item => item.sku);
            const itemNames = new Map(items.map(item => [(item.sku || '').toUpperCase(), item.name]));

            this.stockList = response.itemStockInformations;

            const sku = (itemCode || '').toUpperCase();

            for(const stock of this.stockList) {
                stock.name = itemNames.get(sku);
                stock.available = stock.finalOnHand - (stock.reservedSaleOrd ?? 0);
            }
        } catch (error: any) {
            this.message = error?.data?.errorMessage;

            if([401].includes(error.status)) {
                this.createLoginForm();
            }
        } finally {
            this.operationInProgress.set(false);
        }
    }
}
@Injectable({
    providedIn: 'root'
})
export class MagoOrdersDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog(options?: MagoOrdersInput): Promise<Sales | undefined> {
        if(!options) {
            options = {
                mode: 'orders'
            }
        };

        const config: NonNullableConfigData<any> = {
            ...this.switchMobileDesktopDimensions({ width: options.mode === 'stock' ? '90vw' : '800px' }),
            disableClose: true,
            data: options
        };

        return lastValueFrom(this.dialogRef.open(MagoOrdersDialogComponent, config).afterClosed());
    }
}
